import { styled } from '@airwallex/theme';
import React, { HTMLAttributes } from 'react';
import { css } from '@emotion/react';
import { breakpoints } from '@airwallex/global';
import { white } from '@/components/common/palette';
import { useLargeScreen } from '@/hooks/useMediaQuery';
import { usePaymentSurfaceBackground } from '@/providers/paymentThemeScope';

export const Container = styled.div(
  ({ theme }) => css`
    background: ${theme.colors.white};
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    ${breakpoints.breakpoint.s} {
      padding: 0 ${theme.space.xs};
    }
  `
);

export const containerCss = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

export const desktopCss = css`
  background: white;
  padding: 0px;
  scrollbar-gutter: stable both-edges;
  &::before {
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
    background: ${white};
    content: ' ';
    height: 100%;
    position: fixed;
    right: 0;
    top: 0;
    -webkit-transform-origin: right;
    -ms-transform-origin: right;
    transform-origin: right;
    width: 50%;
    box-shadow: 15px 0 30px 0 rgba(0, 0, 0, 0.18);
  }
`;

/*
 * The fixed right gutter behind the payment column (`desktopCss::before`), with
 * the surface colour pinned to the theme in scope.
 *
 * The gutter is a fixed-position pseudo-element painted at the page level, so it
 * does NOT inherit the payment subtree's `PaymentThemeScope` — it keeps the
 * hardcoded white unless the caller overrides it. On a dark theme that leaves a
 * white right half. Callers that wrap the payment column pass the resolved
 * `background.primary` as `gutterBackground` and this override repaints it;
 * un-themed pages leave it undefined and render the original white.
 *
 * @param gutterBackground The surface colour to pin the gutter to, if any.
 * @return The override rule, or a falsy value when no colour was supplied.
 */
export const desktopGutterCss = (gutterBackground?: string) =>
  gutterBackground &&
  css`
    &::before {
      background: ${gutterBackground};
    }
  `;

/*
 * The mobile page ground.
 *
 * On mobile there is no separate gutter — the payment column is the whole page,
 * laid over `containerCss`'s `min-height: 100vh`. That container is transparent,
 * so anything shorter than the viewport exposes the hardcoded white
 * `html`/`body` from `src/styles/global.tsx` below it. On a dark theme that reads
 * as a white band under the payment surface (PAA-3012).
 *
 * Repaint the container itself with the locked payment paper, so one rule fixes
 * every consumer at once. The merchant LHS paper (`themedBackground`) is
 * deliberately NOT used here: it paints the desktop left column only, and on
 * mobile it would put the merchant page colour behind the payment surface.
 *
 * @param gutterBackground The locked payment paper, if one is in scope.
 * @return The override rule, or a falsy value when no colour was supplied.
 */
export const mobileGroundCss = (gutterBackground?: string) =>
  gutterBackground &&
  css`
    background: ${gutterBackground};
  `;

export const PayByLinkContainer: React.FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...rest }) => {
  return (
    <div
      css={css`
        display: flex;
        position: relative;
        justify-content: center;
        flex-direction: row;
        padding: 120px 0;
        min-height: 100vh;
        ${breakpoints.breakpoint.l} {
          padding: 56px 0;
          justify-content: center;
          align-items: center;
          flex-direction: column;
        }
      `}
      {...rest}
    >
      {children}
    </div>
  );
};

interface SplitScreenContainerProps extends HTMLAttributes<HTMLDivElement> {
  // Override the internal `useLargeScreen()` (≥1200px) check. Pass `!useMobile()`
  // (≥721px) when the page should switch to mobile at the same point as
  // shopify-lpm — the Xero invoice page does this so it stops collapsing into
  // mobile view between 720px–1199px.
  isLargeScreen?: boolean;
  // Override the hardcoded `white` background of the `desktopCss` main panel
  // (the left body column). The themed Xero invoice page passes
  // `semanticColors.background.secondary` — the order-info band surface, which
  // carries the merchant `colorBackground`'s generator-derived shade when
  // configured (see deriveLhsThemeOverride) — so the left body reads as a band
  // distinct from the payment column. The fixed-position `::before` right gutter
  // is intentionally left at its original white so the right payment section
  // keeps the original background (per design). Callers that pass undefined keep
  // the `#fff` panel.
  themedBackground?: string;
  // Override the `desktopCss::before` right gutter — the fixed half-viewport
  // panel behind the payment column — with the locked payment paper. It is
  // painted at the page level, so it does not inherit `PaymentThemeScope`; the
  // themed Xero invoice page passes the payment column's resolved surface
  // (`background.primary`) so a dark payment column is not backed by a white
  // gutter. Callers that pass undefined fall back to the paper the shell
  // publishes, and then to the original white (PAA-3012).
  themedGutter?: string;
  // RFC-252 preview only: drop the mobile `containerCss` `min-height: 100vh` so
  // the embedded preview is content-sized. Without this the mobile preview is
  // viewport-tall, which the parent iframe over-reports (tall empty band + a
  // second scrollbar). Production checkout leaves this false to keep the
  // full-height live page (ADV-6289).
  contentSized?: boolean;
}

export const SplitScreenContainer: React.FC<SplitScreenContainerProps> = ({
  children,
  isLargeScreen: isLargeScreenProp,
  themedBackground,
  themedGutter,
  contentSized,
  ...rest
}) => {
  const detectedIsLargeScreen = useLargeScreen();
  const isLargeScreen = isLargeScreenProp ?? detectedIsLargeScreen;
  // The locked payment paper published by the shell, used when the caller does
  // not pin the surface itself. Without this the container falls back to the
  // hardcoded white `html`/`body` for any page that renders a payment surface but
  // forgets to pass one (and it is what makes this shared rather than per-page,
  // so live/preview and every product get the same ground — PAA-3012).
  const surfacePaper = usePaymentSurfaceBackground();
  const paymentPaper = themedGutter ?? surfacePaper;
  // Compose `desktopCss` with a background override when the preview page
  // requests one. Emotion merges later styles on top of earlier ones, so the
  // override targets `desktopCss`'s main `background` (the left body column)
  // while the rest of the layout (positioning, shadow) stays intact. The
  // fixed `::before` right gutter is deliberately not overridden so the right
  // payment section keeps its original white background (per design).
  const themedDesktopOverride =
    themedBackground &&
    css`
      background: ${themedBackground};
      /* Establish a BFC so the inner content's 120px top margin is contained and
         the merchant background fills the column from the very top instead of
         collapsing out and leaving a neutral band above the content. */
      display: flow-root;
    `;
  // In the content-sized preview, collapse `containerCss`'s mobile
  // `min-height: 100vh` so the page reports its real content height.
  const contentSizedOverride =
    contentSized &&
    css`
      min-height: 0;
    `;
  // The gutter is a `desktopCss::before` pseudo-element, so the override is only
  // meaningful on the desktop branch — skip it on mobile rather than emit a rule
  // that matches nothing.
  const gutterOverride = isLargeScreen ? desktopGutterCss(paymentPaper) : undefined;
  // Mobile has no gutter pseudo-element, so the same surface paints the page
  // ground itself instead — see `mobileGroundCss`.
  const mobileGroundOverride = isLargeScreen ? undefined : mobileGroundCss(paymentPaper);
  const baseCss = isLargeScreen ? desktopCss : containerCss;
  return (
    <div
      css={[baseCss, themedDesktopOverride, gutterOverride, mobileGroundOverride, contentSizedOverride].filter(Boolean)}
      {...rest}
    >
      {children}
    </div>
  );
};
